import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, ChevronDown, Check } from 'lucide-react';

// Positions a dropdown panel relative to a trigger, rendered via portal to
// document.body — escapes the stacking contexts created by backdrop-filter on
// .glass cards, which otherwise paint later siblings (the data grid) on top of
// the dropdown regardless of z-index.
export function useDropdownPortal(open, triggerRef, panelWidth = 220) {
  const [pos, setPos] = useState(null);
  useEffect(() => {
    if (!open || !triggerRef.current) { setPos(null); return; }
    const update = () => {
      const r = triggerRef.current.getBoundingClientRect();
      const alignLeft = r.left + panelWidth <= window.innerWidth - 8;
      setPos({
        top: r.bottom + 4,
        left: alignLeft ? r.left : undefined,
        right: alignLeft ? undefined : Math.max(8, window.innerWidth - r.right),
        minWidth: r.width,
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, triggerRef, panelWidth]);
  return pos;
}

// Outside-click that accounts for the portalled panel living outside the trigger's DOM tree
export function useOutsideClose(open, refs, onClose) {
  useEffect(() => {
    if (!open) return;
    const handler = e => {
      if (refs.every(r => !r.current || !r.current.contains(e.target))) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, refs, onClose]);
}

export function FilterBar({ children, onClear, hasFilters }) {
  return (
    <div className="glass glow-border rounded-2xl p-4">
      <div className="flex flex-wrap gap-3 items-center">
        {children}
        {hasFilters && (
          <button onClick={onClear} className="btn-ghost text-xs" style={{ color: '#ef4444', borderColor: 'rgba(239,68,68,0.2)' }}>
            <X size={13} /> Clear
          </button>
        )}
      </div>
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder }) {
  const [local, setLocal] = useState(value);
  const timer = useRef(null);

  // Sync when parent clears the value (e.g. Clear button)
  useEffect(() => { setLocal(value); }, [value]);

  const handleChange = (v) => {
    setLocal(v);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => onChange(v), 300);
  };

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  return (
    <div className="relative flex-1 min-w-[200px]">
      <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#334155' }} />
      <input value={local} onChange={e => handleChange(e.target.value)} placeholder={placeholder || 'Search...'}
        className="input-dark pl-9" />
    </div>
  );
}

export function FilterSelect({ value, onChange, options, placeholder }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} className="input-dark" style={{ width: 'auto', minWidth: 140 }}>
      <option value="">{placeholder || 'All'}</option>
      {options.map(o => (
        <option key={o.value || o} value={o.value || o}>{o.label || String(o).replace(/_/g, ' ')}</option>
      ))}
    </select>
  );
}

// Multi-select dropdown filter — value is an array; onChange receives new array.
// The panel is portalled to document.body so it always paints above data grids.
export function MultiFilterSelect({ value = [], onChange, options, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const panelRef = useRef(null);
  const pos = useDropdownPortal(open, ref, 220);
  useOutsideClose(open, [ref, panelRef], () => setOpen(false));
  const toggle = (v) => {
    onChange(value.includes(v) ? value.filter(x => x !== v) : [...value, v]);
  };
  const label = value.length === 0 ? (placeholder || 'All') : value.length === 1
    ? String(value[0]).replace(/_/g, ' ')
    : `${value.length} selected`;
  return (
    <div ref={ref} style={{ position: 'relative', minWidth: 140 }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        className="input-dark"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, cursor: 'pointer', width: '100%', textAlign: 'left' }}>
        <span style={{ color: value.length ? '#e2e8f0' : '#475569', fontSize: 13, textTransform: 'capitalize' }}>{label}</span>
        <ChevronDown size={12} style={{ color: '#475569', flexShrink: 0 }}/>
      </button>
      {open && pos && createPortal(
        <div ref={panelRef} style={{ position: 'fixed', top: pos.top, left: pos.left, right: pos.right, zIndex: 99999, minWidth: 180, maxHeight: 300, overflowY: 'auto', background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: '4px 0', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
          {options.map(o => {
            const v = o.value || o; const l = o.label || String(o).replace(/_/g, ' ');
            const selected = value.includes(v);
            return (
              <button key={v} type="button" onClick={() => toggle(v)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 12px', background: selected ? 'rgba(99,102,241,0.15)' : 'transparent', border: 'none', cursor: 'pointer', color: selected ? '#818cf8' : '#94a3b8', fontSize: 12, textTransform: 'capitalize', textAlign: 'left' }}>
                <span style={{ width: 14, height: 14, border: `1px solid ${selected ? '#818cf8' : '#334155'}`, borderRadius: 4, background: selected ? 'rgba(99,102,241,0.3)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {selected && <Check size={9} style={{ color: '#818cf8' }}/>}
                </span>
                {l}
              </button>
            );
          })}
          {value.length > 0 && (
            <button type="button" onClick={() => onChange([])}
              style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '7px 12px', background: 'transparent', border: 'none', borderTop: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer', color: '#ef4444', fontSize: 11, marginTop: 4 }}>
              <X size={10}/> Clear selection
            </button>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

export function PageHeader({ title, count, children }) {
  return (
    <div className="flex items-center justify-between" style={{ animation: 'fadeIn 0.3s ease-out' }}>
      <div>
        <h1 className="text-xl font-bold gradient-text">{title}</h1>
        {count !== undefined && <p className="text-xs mt-0.5" style={{ color: '#334155' }}>{count} total records</p>}
      </div>
      <div className="flex gap-2">{children}</div>
    </div>
  );
}
